import { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import { FaEye, FaPen } from "react-icons/fa";

import { Button } from "../../../components/Form";
import { getVehicles } from "../../../rtk-slice/globalSlice";
import { postData } from "../../../utils/api";
import { errorToast, successToast } from "../../../utils/toastFunc";
import "../UserManagement.scss";
import TextBox from "../../../components/Form/OldTextBox";

const RatesTab = ({
  defaultValues = { rates: [] },
  driverId,
  submitUrl,
  viewEditMode,
  setKey,
}) => {
  const vehicleList = useSelector(getVehicles);
  const [viewMode, setViewMode] = useState(viewEditMode);

  const toggleViewMode = () =>
    setViewMode((prevViewMode) => (prevViewMode === "edit" ? "view" : "edit"));

  // Validation schema for the form
  const validationSchema = Yup.object().shape({
    rates: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Value is mandatory"),
        rate: Yup.number()
          .typeError("Enter a Number")
          .nullable() // Allow null/empty values - we'll handle defaults in submission
          .transform((value) => (isNaN(value) ? null : value)), // Convert NaN to null
        notes: Yup.string(),
      })
    ),
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    reValidateMode: "onChange",
    defaultValues: {},
  });

  // Initialize form with proper default values
  useEffect(() => {
    if (driverId == null || !vehicleList?.length) return;

    const initialValues = { rates: [] };

    vehicleList.forEach((vehicle, index) => {
      const matchingRate = defaultValues?.rates?.find(
        (rate) =>
          rate.vehicleTypeId === vehicle.id || rate.name === vehicle.name
      );

      initialValues.rates[index] = {
        name: vehicle.name,
        rate: matchingRate?.rate ?? 0,
        notes: matchingRate?.notes ?? "",
      };
    });

    // Serialize to compare
    const newDataStr = JSON.stringify(initialValues);
    const currentDataStr = JSON.stringify(getValues()); // from react-hook-form

    if (newDataStr !== currentDataStr) {
      reset(initialValues);
    }
  }, [vehicleList, defaultValues, reset, driverId, getValues]);

  /**
   * Get vehicle ID by name
   */
  const getIdByName = (name) => {
    const found = vehicleList.find((item) => item.name === name);
    return found ? found.id : null;
  };

  const onSubmitFunction = async (formData) => {
    // Format data for submission and ensure default zero for empty rates
    const data = formData.rates.map((item) => {
      return {
        ...item,
        // Set rate to 0 if it's null, undefined, empty string, or NaN
        rate:
          item.rate === null ||
          item.rate === undefined ||
          item.rate === "" ||
          isNaN(item.rate)
            ? 0
            : Number(item.rate),
        vehicleTypeId: getIdByName(item.name),
      };
    });

    const fullSubmitUrl = submitUrl + `?driverId=${driverId}`;
    const response = await postData(fullSubmitUrl, data);

    if (response?.data) {
      successToast({ mes: "Rates Saved Successfully" });
      if (!viewEditMode) {
        // if creating new User
        setViewMode("view"); // to enable view mode after setting rates
        setKey("deductions"); // moving to another tab
      }
    } else {
      errorToast({ mes: "Try Again" });
    }
  };

  if (!vehicleList || vehicleList.length === 0) {
    return <div>Loading vehicle rates...</div>;
  }

  return (
    <>
      <form
        autoComplete="off"
        onSubmit={handleSubmit(onSubmitFunction)}
        className="blur-bg p-3"
      >
        {!viewEditMode && (
          <button
            title={viewMode === "edit" ? "View" : "Edit"}
            type="button"
            className="btn btn-primary p-1 me-1 lh-1"
            onClick={toggleViewMode}
          >
            {viewMode === "edit" ? <FaEye size={20} /> : <FaPen size={20} />}
          </button>
        )}

        <div className="col">
          <div className="form-group d-flex bold-text">
            <div className="col-1 p-2 center-items">S.N</div>
            <div className="col-3 p-2 center-items">Vehicle Type</div>
            <div className="col-2 p-2 center-items">Rates</div>
            <div className="col-6 p-2 center-items">Notes</div>
          </div>

          {vehicleList.map((item, index) => (
            <div key={item.id || index} className="form-group d-flex row">
              <div className="col-1 p-2 center-items">
                <div className="d-inline-block">{index + 1}</div>
              </div>

              <div className="col-3 p-2">
                <TextBox
                  id={`rates[${index}].name`}
                  staticVal={item.name}
                  disabled={true}
                  register={register}
                  errors={errors}
                  setValue={setValue}
                />
              </div>

              <div className="col-2 p-2">
                <TextBox
                  id={`rates[${index}].rate`}
                  type="number"
                  disabled={viewMode === "view"}
                  register={register}
                  errors={errors}
                  placeholder="0"
                />
              </div>

              <div className="col-5 p-2">
                <TextBox
                  register={register}
                  disabled={viewMode === "view"}
                  errors={errors}
                  id={`rates[${index}].notes`}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="row p-4">
          <div className="col-10"></div>
          <div className="ml-auto col-2">
            {viewMode !== "view" && (
              <Button
                type="submit"
                className="buttons"
                title={viewMode === "edit" ? "Save" : "Submit"}
              />
            )}
          </div>
        </div>
      </form>
    </>
  );
};

export default RatesTab;
